import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Lock, Menu, X, Search } from 'lucide-react';
import { AdvertiseModal } from './AdvertiseModal';
import { enqueueAdLoad } from '@/lib/adQueue';
import { runScriptsInSandbox } from '@/lib/sandboxedAd';

const LOGO_URL = "/coyp-logo.png";

const MAIN_NAV_ITEMS = [
  { path: '/', label: 'HOME PAGE', testId: 'nav-feed' },
  { path: '/allstories', label: 'NEWS FEED', testId: 'nav-cincy-feed' },
  { path: '/worldstar', label: 'WORLDSTAR', testId: 'nav-worldstar' },
  { path: '/check-your-claims', label: 'CLAIMS', testId: 'nav-claims', badge: 'FREE $' },
  { path: '/merch', label: 'MERCH', testId: 'nav-merch' },
  { path: '/about', label: 'ABOUT', testId: 'nav-about' },
  { path: '/contact', label: 'CONTACT', testId: 'nav-contact' },
  { path: '/privacy-policy', label: 'PRIVACY', testId: 'nav-privacy-policy' },
];

const MOBILE_NAV_ITEMS = [
  { path: '/', label: 'HOME PAGE', testId: 'mobile-nav-feed' },
  { path: '/allstories', label: 'COYP NEWS FEED', testId: 'mobile-nav-cincy-feed' },
  { path: '/worldstar', label: 'CINCY WORLDSTAR', testId: 'mobile-nav-worldstar' },
  { path: '/check-your-claims', label: 'CHECK YOUR CLAIMS', testId: 'mobile-nav-claims' },
  { path: '/merch', label: 'MERCH', testId: 'mobile-nav-merch' },
  { path: '/about', label: 'ABOUT', testId: 'mobile-nav-about' },
  { path: '/contact', label: 'CONTACT', testId: 'mobile-nav-contact' },
  { path: '/privacy-policy', label: 'PRIVACY POLICY', testId: 'mobile-nav-privacy-policy' },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [advertiseModalOpen, setAdvertiseModalOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (!trimmed) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    setSearchOpen(false);
  };

  return (
    <>
    <header className="sticky top-0 z-50 bg-black border-b border-[#222]" data-testid="main-header">
      {/* Main header */}
      <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
        {/* Lock Icon - Admin Access */}
        <Link 
          to="/admin" 
          className="text-yellow-500 hover:text-yellow-400" 
          data-testid="lock-icon"
          title="Admin"
        >
          <Lock size={20} />
        </Link>

        {/* Logo */}
        <Link to="/" className="flex-1 flex justify-center" data-testid="logo-link">
          <img 
            src={LOGO_URL} 
            alt="Check On Yo Peoples TV" 
            className="h-16 sm:h-24 object-contain"
          />
        </Link>

        {/* Search + Menu */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSearchOpen((v) => !v)}
            className="text-gray-300 hover:text-yellow-500 transition-colors p-1"
            data-testid="search-icon-toggle"
            title="Search"
          >
            {searchOpen ? <X size={22} /> : <Search size={22} />}
          </button>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white p-1"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Search Bar */}
      {searchOpen && (
        <div className="border-t border-[#222] bg-[#0a0a0a] px-4 py-3" data-testid="search-bar">
          <form onSubmit={handleSearchSubmit} className="max-w-4xl mx-auto flex items-center gap-2">
            <Search size={16} className="text-gray-500 shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search stories..."
              className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-gray-600"
              data-testid="search-input"
            />
            <button
              type="submit"
              className="px-3 py-1.5 bg-yellow-500 text-white text-xs font-bold hover:bg-yellow-600 transition-colors"
              data-testid="search-submit-btn"
            >
              Search
            </button>
          </form>
        </div>
      )}

      {/* Navigation */}
      <nav className="border-t border-[#222]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-center gap-3 sm:gap-5 py-3 flex-wrap">
            {MAIN_NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative text-xs sm:text-sm font-medium transition-colors duration-200 py-1 ${
                  isActive(item.path) ? 'text-yellow-500' : 'text-gray-300 hover:text-white'
                }`}
                data-testid={item.testId}
              >
                {item.label}
                {item.badge && (
                  <span className="absolute -top-2 -right-8 px-1 py-0.5 text-[8px] font-bold bg-green-500 text-black rounded-sm animate-pulse leading-none">
                    {item.badge}
                  </span>
                )}
                {isActive(item.path) && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-500 animate-scale-in" />}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Leaderboard Ad (728x90) - kept inside the sticky header so it stays pinned/visible while scrolling for maximum viewability */}
      <div className="w-full flex justify-center py-2 bg-black border-t border-[#222] overflow-hidden" data-testid="leaderboard-ad">
        <LeaderboardAd />
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="border-t border-[#222] bg-black" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-4">
            {MOBILE_NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="block text-gray-400 hover:text-white text-sm font-medium"
                onClick={() => setMobileMenuOpen(false)}
                data-testid={item.testId}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>

    {/* Advertise Here — Netflix-style cinematic banner */}
    <button
      onClick={() => setAdvertiseModalOpen(true)}
      className="block w-full relative overflow-hidden group"
      data-testid="advertise-here-banner"
      style={{ background: 'linear-gradient(90deg, #000 0%, #1a1200 20%, #2d1f00 50%, #1a1200 80%, #000 100%)' }}
    >
      {/* Animated shimmer sweep */}
      <div className="absolute inset-0 overflow-hidden opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(202,138,4,0.15) 50%, transparent 100%)',
          backgroundSize: '200% 100%',
          animation: 'shimmerSweep 2s ease-in-out infinite',
        }}
      />
      {/* Subtle red glow pulse */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(202,138,4,0.08) 0%, transparent 70%)',
          animation: 'glowPulse 3s ease-in-out infinite',
        }}
      />
      {/* Side accents */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-yellow-600 pointer-events-none" style={{ boxShadow: '0 0 12px rgba(202,138,4,0.6)' }} />
      <div className="absolute right-0 top-0 bottom-0 w-1 bg-yellow-600 pointer-events-none" style={{ boxShadow: '0 0 12px rgba(202,138,4,0.6)' }} />

      <div className="relative z-10 py-2.5 px-3 sm:py-3 sm:px-4 flex items-center justify-center gap-2 sm:gap-3">
        <span className="text-yellow-500 text-sm sm:text-lg font-black tracking-tight" style={{ textShadow: '0 0 20px rgba(202,138,4,0.5)' }}>▶</span>
        <span className="text-white text-[10px] sm:text-sm font-bold tracking-[0.15em] sm:tracking-[0.2em] uppercase" style={{ textShadow: '0 0 10px rgba(255,255,255,0.1)' }}>
          Advertise With Us
        </span>
        <span className="hidden sm:inline text-gray-500 text-xs">|</span>
        <span className="hidden sm:inline text-gray-400 text-xs tracking-wide group-hover:text-yellow-400 transition-colors duration-300">
          checkonyopeoples1@gmail.com
        </span>
        <span className="text-yellow-500 text-sm sm:text-lg font-black tracking-tight" style={{ textShadow: '0 0 20px rgba(202,138,4,0.5)' }}>◀</span>
      </div>
    </button>

    <AdvertiseModal open={advertiseModalOpen} onOpenChange={setAdvertiseModalOpen} />
    </>
  );
};

const LeaderboardAd = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    container.innerHTML = '';
    let cancelled = false;
    let cleanupSandbox = () => {};

    enqueueAdLoad(() => new Promise((resolve) => {
      if (cancelled) return resolve();
      cleanupSandbox = runScriptsInSandbox(container, [
        {
          innerHTML: `atOptions = {
            'key' : '0b18b71c7d1fe63eafc86b4e0adfc131',
            'format' : 'iframe',
            'height' : 90,
            'width' : 728,
            'params' : {}
          };`,
        },
        { src: 'https://www.highperformanceformat.com/0b18b71c7d1fe63eafc86b4e0adfc131/invoke.js' },
      ]);
      setTimeout(resolve, 1500); // safety fallback so the queue never stalls
    }));

    return () => { cancelled = true; cleanupSandbox(); };
  }, []);

  return <div ref={containerRef} className="w-full max-w-[728px]" />;
};

export default Header;
